import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Loader, Undo } from "lucide-react";
import { catchError } from "@/lib/errors";
import { questionsRepo, answersRepo, dilemmasRepo } from "@/lib/repositories";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import BottomFixed from "@/components/bottom-fixed";
import DilemmaFormAnswersSection from "./dilemma-form-answers-section";
import { useDilemmasFormStore } from "../../stores/useDilemmaFormStore";
import { DILEMMAS_STATUS } from "../../constants";
import { hasStep2Changes } from "../../utils";
import { queryClient } from "@/lib/query-client";

export interface IAnswerErrors {
  [answerId: number]: { answer?: string; feedback?: string };
}

const DilemmaFormStepTwo = () => {
  const { t } = useTranslation();
  const step2Data = useDilemmasFormStore((s) => s.step2Data);
  const setStep2Data = useDilemmasFormStore((s) => s.setStep2Data);
  const setCurrentStep = useDilemmasFormStore((s) => s.setCurrentStep);
  const createdDilemma = useDilemmasFormStore((s) => s.createdDilemma);
  const initialDilemma = useDilemmasFormStore((s) => s.initialDilemma);
  const setCreatedDilemma = useDilemmasFormStore((s) => s.setCreatedDilemma);
  const isEditMode = useDilemmasFormStore((s) => s.isEditMode);
  const reviewComment = useDilemmasFormStore((s) => s.reviewComment);
  const setIsStep2Dirty = useDilemmasFormStore((s) => s.setIsStep2Dirty);
  const reset = useDilemmasFormStore((s) => s.reset);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const canEdit = !createdDilemma || createdDilemma.can_edit;
  const isSubmittedReviewable =
    createdDilemma?.source === "mobile" &&
    createdDilemma?.in_submission_process === true &&
    createdDilemma?.can_publish === false;
  const [answerErrors, setAnswerErrors] = useState<IAnswerErrors>({});
  const [globalAnswerError, setGlobalAnswerError] = useState("");

  const formAnswers = step2Data.answers;
  const setFormAnswers = (
    action: React.SetStateAction<typeof step2Data.answers>,
  ) => {
    const next =
      typeof action === "function" ? action(step2Data.answers) : action;
    setStep2Data({ answers: next });
  };

  const dilemmaFormStep2Schema = z.object({
    question: z.string().min(1, t("modules.dilemmas.errors.question_required")),
  });

  const formStep2 = useForm<z.infer<typeof dilemmaFormStep2Schema>>({
    resolver: zodResolver(dilemmaFormStep2Schema),
    defaultValues: { question: step2Data.question },
  });

  const watchedQuestion = useWatch({
    control: formStep2.control,
    name: "question",
  });

  useEffect(() => {
    setIsStep2Dirty(
      hasStep2Changes(initialDilemma, {
        question: watchedQuestion ?? "",
        answers: formAnswers.map((a) => ({
          payload: a.answer,
          isCorrect: a.isCorrect,
        })),
      }),
    );
  }, [watchedQuestion, formAnswers, initialDilemma, setIsStep2Dirty]);

  useEffect(() => {
    return () => {
      if (!formStep2.formState.isDirty) return;
      setStep2Data({ question: formStep2.getValues("question") });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validateAnswers(): boolean {
    const errors: IAnswerErrors = {};
    for (const a of formAnswers) {
      const fieldErrors: { answer?: string; feedback?: string } = {};
      if (!a.answer.trim()) {
        fieldErrors.answer = t("modules.dilemmas.errors.answer_required");
      }
      if (!a.feedback.trim()) {
        fieldErrors.feedback = t("modules.dilemmas.errors.feedback_required");
      }
      if (Object.keys(fieldErrors).length > 0) {
        errors[a.id] = fieldErrors;
      }
    }
    const hasNoCorrectAnswer = !formAnswers.some((a) => a.isCorrect);
    if (hasNoCorrectAnswer) {
      setGlobalAnswerError(t("modules.dilemmas.errors.at_least_one_correct"));
    } else {
      setGlobalAnswerError("");
    }

    setAnswerErrors(errors);
    return Object.keys(errors).length === 0 && !hasNoCorrectAnswer;
  }

  function saveToStore() {
    setStep2Data({ question: formStep2.getValues("question") });
  }

  async function handleReject() {
    if (!createdDilemma) return;
    setIsLoading(true);
    const [error] = await catchError(
      dilemmasRepo.patch(createdDilemma.id, { status: DILEMMAS_STATUS.DRAFT }),
    );
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["submissions"] });
    toast.success(t("messages.dilemma_reject_success"));
    reset();
    navigate(ROUTES.SUBMISSIONS);
    setIsLoading(false);
  }

  function handlePrevious() {
    saveToStore();
    setCurrentStep(1);
  }

  async function onSubmit(data: z.infer<typeof dilemmaFormStep2Schema>) {
    if (!validateAnswers()) return;
    if (!createdDilemma) return;

    setIsLoading(true);

    const apiQuestion = createdDilemma.questions[0];

    const onlyCommentsChanged =
      (isEditMode || isSubmittedReviewable) &&
      data.question === apiQuestion.payload &&
      formAnswers.every((formAnswer, index) => {
        const apiAnswer = apiQuestion.answers[index];
        return (
          formAnswer.answer === apiAnswer.payload &&
          formAnswer.isCorrect === apiAnswer.is_correct
        );
      });

    if (!onlyCommentsChanged) {
      const [questionError] = await catchError(
        questionsRepo.patch(apiQuestion.id, { payload: data.question }),
      );
      if (questionError) {
        toast.error(handleApiError(questionError).message);
        setIsLoading(false);
        return;
      }
    }

    // Update all answers in parallel
    const answerPromises = formAnswers.map((formAnswer, index) => {
      const apiAnswer = apiQuestion.answers[index];
      return answersRepo.patch(apiAnswer.id, {
        payload: formAnswer.answer,
        is_correct: formAnswer.isCorrect,
        comment: formAnswer.feedback,
      });
    });

    const [answersError] = await catchError(Promise.all(answerPromises));
    if (answersError) {
      toast.error(handleApiError(answersError).message);
      setIsLoading(false);
      return;
    }

    if (isEditMode) {
      const [reviewCommentError] = await catchError(
        dilemmasRepo.patch(createdDilemma.id, {
          review_comment: reviewComment,
        }),
      );
      if (reviewCommentError) {
        toast.error(handleApiError(reviewCommentError).message);
        setIsLoading(false);
        return;
      }
    }

    const [refreshError, refreshedDilemma] = await catchError(
      dilemmasRepo.fetchOne(createdDilemma.id),
    );
    if (refreshError) {
      toast.error(handleApiError(refreshError).message);
      setIsLoading(false);
      return;
    }
    setCreatedDilemma(refreshedDilemma);

    saveToStore();
    if (isEditMode) {
      await queryClient.invalidateQueries({
        queryKey: ["dilemma", createdDilemma?.id],
      });
      toast.success(t("messages.dilemma_edit_success"));
    }
    setCurrentStep(3);
    setIsLoading(false);
  }

  return (
    <div className="flex flex-col gap-4">
      <form
        id="dilemma-form-step-2"
        onSubmit={formStep2.handleSubmit(onSubmit, () =>
          toast.error(t("modules.dilemmas.errors.form_incomplete")),
        )}
      >
        <FieldGroup className="gap-4">
          <Controller
            name="question"
            control={formStep2.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="question-input">
                  {t("modules.dilemmas.form.the_question")}{" "}
                  <span className="text-destructive">*</span>
                </FieldLabel>
                <Input
                  {...field}
                  className="h-12"
                  id="question-input"
                  aria-invalid={fieldState.invalid}
                  disabled={!canEdit}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <hr className="-mx-4" />
          <div className="p-3 flex gap-4 bg-blue-50 border border-blue-300 rounded-md">
            <div className="size-6 not-first-of-type:shrink-0 italic font-serif items-center justify-center flex rounded-full bg-blue-600 text-white">
              i
            </div>
            <p className="text-gray-600 text-sm mt-0.5 flex-1">
              {t("modules.dilemmas.form.answers_infos")}
            </p>
          </div>

          {globalAnswerError && (
            <p className="text-sm text-destructive">{globalAnswerError}</p>
          )}

          <DilemmaFormAnswersSection
            formAnswers={formAnswers}
            setFormAnswers={setFormAnswers}
            errors={answerErrors}
            disabled={!canEdit}
          />
        </FieldGroup>
      </form>
      <BottomFixed className="bg-white">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="border-primary border text-primary"
            disabled={isLoading}
            type="button"
            onClick={handlePrevious}
          >
            <ArrowLeft />
            {t("keys.previous")}
          </Button>
          <div className="flex items-center gap-3">
            {isSubmittedReviewable && (
              <Button
                variant="primary_light"
                disabled={isLoading}
                type="button"
                onClick={handleReject}
              >
                {isLoading ? (
                  <Loader className="animate-spin" />
                ) : (
                  <Undo className="size-4" />
                )}
                {t("keys.reject_dilemma")}
              </Button>
            )}
            <Button
              disabled={isLoading || !canEdit}
              type="submit"
              form="dilemma-form-step-2"
            >
              {isLoading ? <Loader className="animate-spin" /> : <ArrowRight />}
              {t("keys.next")}
            </Button>
          </div>
        </div>
      </BottomFixed>
    </div>
  );
};

export default DilemmaFormStepTwo;
